import { z } from 'zod';
import superjson from 'superjson';
import { Selectable } from 'kysely';
import { GeneratedImages } from '../../helpers/schema';

export const schema = z.object({
  sessionId: z.string(),
  prompt: z.string().min(5).max(2000),
  width: z.number().min(256).max(4096).optional().default(1024),
  height: z.number().min(256).max(4096).optional().default(1024),
  saveToSession: z.boolean().optional().default(true),
  // Image provider: 'modelslab' (default/primary), 'flux' (legacy fallback), or 'gemini'
  provider: z.enum(['modelslab', 'flux', 'gemini']).optional().default('modelslab'),
  // Image-to-image editing options
  sourceImage: z.string().optional(), // Base64 data URL of source image to edit
  logoImage: z.string().optional(), // Base64 data URL of logo to overlay/incorporate
  logoPosition: z
    .enum(['top-left', 'top-right', 'bottom-left', 'bottom-right', 'center', 'custom'])
    .optional()
    .default('bottom-right'),
  logoScale: z.number().min(0.05).max(0.5).optional().default(0.15), // Logo size relative to image
  mode: z
    .enum(['generate', 'edit', 'banner', 'flyer', 'promo', 'cover'])
    .optional()
    .default('generate'),
  style: z.string().optional(), // Additional style guidance (e.g., "professional", "playful", "minimalist")
});

export type InputType = z.infer<typeof schema>;

export type OutputType = {
  image: {
    id?: string;
    imageUrl: string;
    prompt: string;
    width: number;
    height: number;
    mode?: string;
    hasLogo?: boolean;
  };
};

export const generateImage = async (input: InputType, init?: RequestInit): Promise<OutputType> => {
  const result = await fetch('/_api/images/generate', {
    method: 'POST',
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
    body: superjson.stringify(input),
  });
  if (!result.ok) {
    const errorObject = superjson.parse<{ error: string }>(await result.text());
    throw new Error(errorObject.error);
  }
  return superjson.parse<OutputType>(await result.text());
};
